import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shared/card';
import { ChartConfig, ChartContainer, ChartTooltipContent } from '@/components/shared/chart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Customized } from 'recharts';
import { ConsolidatedMetrics } from '@/types';
import { DownloadOptionsPopup } from './DownloadOptionsPopup';
import { ShareOptionsPopup } from './ShareOptionsPopup';
import { downloadChartSection, downloadAllChartsWithWatermark, downloadChartWithCanvasWatermark } from '@/utils/chartDownload';
import { useShareUrl } from '@/hooks/useShareUrl';
import { useDownloadWithAuth } from '@/hooks/useDownloadWithAuth';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { InfoIconWithTooltip } from '@/components/shared/InfoIconWithTooltip';
import { copyToClipboardAndToast, createChartShareUrl, createChartId } from '@/utils/shareUtils';
import { calculateChartScale, ScaleResult } from '@/utils/chartScaling';
import AboutDataCard from '@/components/shared/AboutTheData';
import { chartsDataDefinitions } from '../data/OverTimeAboutTheDataDefs';
import { ChartTooltipCard } from '@/components/shared/ChartTooltipCard';
import { Place } from '@/types';
import { formatPlaceName } from '@/lib/utils';

interface OverTimeChartCardProps {
  category: string;
  title: string;
  subtitle: string;
  data: ConsolidatedMetrics[];
  selectedPlace: Place;
  metric?: keyof ConsolidatedMetrics;
  dataFormatter?: (data: ConsolidatedMetrics) => number;
  metricUnits: string;
}

const CustomTooltip = ({ active, payload, label, yAxisLabel, metricUnits }: any) => {
  if (active && payload && payload.length) {
    const formattedValue = payload[0].value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });

    return (
      <ChartTooltipCard
        year={label}
        metricLabel={yAxisLabel}
        value={`${formattedValue}${metricUnits}`}
      />
    );
  }

  return null;
};

const GradientCursor = (props: any) => {
  const { points, height, payload, yAxisConfig } = props;
  
  // Guard against missing or malformed data
  if (!points || !points[0] || !payload || !payload[0] || !yAxisConfig) return null;
  
  const { x } = points[0];
  const dataValue = payload[0].value; // Actual data value
  
  // Calculate proportional height: (value - min) / (max - min) * height
  const { min, max } = yAxisConfig;
  const proportion = (dataValue - min) / (max - min);
  const proportionalHeight = proportion * height;
  
  return (
    <rect 
      fill="url(#gradient)" 
      fillOpacity={0.7} 
      width={40} 
      height={proportionalHeight} // Proportional to data value on axis scale
      x={x - 20} // Center the 40px wide bar on cursor position
      y={height - proportionalHeight + 5} // Start from bottom of proportional area
      rx={2} // Rounded corners
    />
  );
};

const OutlinedDot = (props) => {
  const { cx, cy, stroke } = props;
  return (
    <g>
      <circle cx={cx} cy={cy} r={9} fill="var(--color-value)" />
      <circle cx={cx} cy={cy} r={6} fill="white" />
    </g>
  );
};

const calculateYAxis = (data: { year: number; value: number }[], title: string): ScaleResult => {
  if (!data?.length) {
    return { ticks: [], min: 0, max: 0, hasNegative: false, hasPositive: false, crossesZero: false };
  }

  const values = data.map(d => d.value);

  return calculateChartScale(values, {
    tickCount: 6,
    padding: 0.1,
    forceZeroMin: true,
  });
};

export const OverTimeChartCard: React.FC<OverTimeChartCardProps> = ({
  category,
  title,
  subtitle,
  data,
  metric,
  dataFormatter,
  metricUnits,
  selectedPlace,
}) => {
  
  const chartRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadPopupOpen, setIsDownloadPopupOpen] = useState(false);
  const [isAboutDataOpen, setIsAboutDataOpen] = useState(false);

  const shareLink = useShareUrl();
  const { executeWithAuth } = useDownloadWithAuth();
  const aboutData = chartsDataDefinitions[title];

  const chartData = data
    .filter(d => d.year >= 2019)
    .map(d => ({
      year: d.year,
      value: dataFormatter ? dataFormatter(d) : (metric ? d[metric] as number : 0),
    }))
    .reverse();


  const yAxisConfig = useMemo(() => calculateYAxis(chartData, title), [chartData, title]);

  const chartConfig = {
    value: {
      label: title,
      color: '#CA0079',
    },
  } satisfies ChartConfig;

  const downloadSingleChart = async () => {
    if (!chartRef.current) {
      console.log('No chart ref found');
      return;
    }
    
    console.log('Starting download for chart:', title);
    setIsDownloading(true);
    
    try {
      // Use canvas-based watermark to avoid DOM layout interference with our positioning fixes
      await downloadChartWithCanvasWatermark(chartRef.current, `${title.toLowerCase().replace(/\s+/g, '-')}`, {
        watermarkOptions: {
          opacity: 1.0, 
          size: { width: 200, height: 60 }
        },
        includeTimestamp: false,
        disableWatermark: true, 
      });
    } catch (error) {
      console.error('Error in handleDownload:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadAllCharts = async () => {
    console.log('[downloadAllCharts] Starting individual chart downloads with watermarks');
    setIsDownloading(true);
    try {
      await downloadAllChartsWithWatermark(
        'overtime-charts-section',
        'overtime-charts',
        {
          watermarkOptions: {
            opacity: 0.5,
            size: { width: 300, height: 90 }
          },
          useCanvasWatermark: true, // Use canvas watermarks for consistency with individual downloads
          includeTimestamp: true
        }
      );
    } catch (error) {
      console.error('[downloadAllCharts] Error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Download entire section as single image
  const downloadSectionAsImage = async () => {
    console.log('[downloadSectionAsImage] Capturing full overtime chart section');
    setIsDownloading(true);
    try {
      const section = document.getElementById('overtime-charts-section');
      if (!section) {
        console.warn('Overtime chart section not found');
        return;
      }
      await downloadChartSection(section, 'overtime-charts');
    } catch (error) {
      console.error('[downloadSectionAsImage] Error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadOption = async (option: 'single' | 'all') => {
    setIsDownloadPopupOpen(false);
    if (option === 'single') {
      await executeWithAuth(downloadSingleChart, 'single');
    } else {
      await executeWithAuth(downloadSectionAsImage, 'all'); // Back to section-as-one-canvas
    }
  };

  const handleDownloadButtonClick = () => {
    if (isDownloading) return; // guard against concurrent downloads
    setIsDownloadPopupOpen(true);
  };

  return (
    <Card 
      ref={chartRef} 
      id={createChartId(title, 'ot')}
      data-chart-title={title} 
      className="relative bg-white rounded-[20px]"
    >
      <header className="w-full px-6 relative flex-shrink-0 flex flex-col after:content-[''] after:absolute after:bottom-0 after:inset-x-6 after:h-[4px] after:bg-gradient-to-r after:from-[#CA0079] after:to-[#F1AB11] after:rounded-full pb-6">
        <div className="w-full flex-shrink-0">
          <span className="gap-2.5 text-xs text-white font-medium whitespace-nowrap uppercase tracking-[0.48px] leading-[1.3] bg-[#CA0079] px-3 py-1.5 rounded-[20px] mt-6 mb-0 font-geist-mono inline-flex items-center justify-center category-bubble">
            <span className="category-bubble-text">{category}</span>
          </span>
        </div>
        
        <div className="flex w-full flex-col items-stretch text-[#212121] leading-[1.3] mt-4">
          <h3 className="text-[28px] font-semibold font-geist">
            {title}
          </h3>
          <p className="text-xl font-normal mt-2.5 font-geist opacity-70 min-h-[52px]">
            {subtitle} for {formatPlaceName(selectedPlace.name)}. Brought to you by Govstream.ai.
          </p>
        </div>
      </header>
      <div className="absolute top-6 right-6 flex items-center gap-4 chart-action-buttons">
        <button
          aria-label="Download chart"
          onClick={handleDownloadButtonClick}
          disabled={isDownloading}
          className="relative"
        >
          {isDownloading ? (
            <LoadingSpinner size="sm" className="text-gray-600" />
          ) : (
            <span className="material-symbols-outlined w-6 h-6 text-2xl opacity-70 hover:opacity-100 transition-opacity">
              download
            </span>
          )}
        </button>
        <button aria-label="Share chart" onClick={async () => await copyToClipboardAndToast(createChartShareUrl(shareLink, title, 'ot'), title)}>
          <span className="material-symbols-outlined w-6 h-6 text-2xl opacity-70 hover:opacity-100 transition-opacity">
            share
          </span>
        </button>
        <InfoIconWithTooltip 
          tooltipText="Learn more about the data and how it's defined."
          onClick={() => setIsAboutDataOpen(true)}
        />
      </div>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full pr-4 pt-6">
          <LineChart
            accessibilityLayer
            data={chartData}
            margin={{
              top: 5,
              right: 20,
              left: 15,
              bottom: 5,
            }}
          >
            <defs>
              <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#CA0079" stopOpacity={0.8} />
                <stop offset="100%" stopColor="#CA0079" stopOpacity={0.2} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="year"
              tickLine={false}
              axisLine={{ opacity: 0.7 }}
              tickMargin={10}
              padding={{ left: 20, right: 20 }}
            />
            <YAxis
              domain={[yAxisConfig.min, yAxisConfig.max]}
              ticks={yAxisConfig.ticks.map(t => t.value)}
              tickFormatter={(value) => {
                let formattedValue;
                if (value >= 1000000) formattedValue = `${(value / 1000000).toFixed(1)}M`;
                else if (value >= 1000) formattedValue = `${(value / 1000)}K`;
                else formattedValue = value.toFixed(1);
                return `${formattedValue}${metricUnits}`;
              }}
              tickMargin={20}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              cursor={<GradientCursor yAxisConfig={yAxisConfig} />}
              content={<CustomTooltip yAxisLabel={title} metricUnits={metricUnits} />}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="var(--color-value)"
              strokeWidth={4.5}
              dot={<OutlinedDot />}
              activeDot={(props: any) => {
                console.log('🔴 ActiveDot - called with props:', props);
                return <OutlinedDot {...props} />;
              }}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
      <DownloadOptionsPopup
        isOpen={isDownloadPopupOpen}
        onClose={() => setIsDownloadPopupOpen(false)}
        onDownload={handleDownloadOption}
        chartName={title}
      />
      {/* Share options modal - kept for future use */}
      <ShareOptionsPopup
        isOpen={false}
        onClose={() => {}}
        shareLink={shareLink}
        chartName={title}
      />
      {isAboutDataOpen && aboutData && (
        <AboutDataCard
          onClose={() => setIsAboutDataOpen(false)}
          aboutDescription={subtitle}
          sources={aboutData.sources}
          metricDefinition={aboutData.metricDefinition}
          calculationFormula={aboutData.calculationFormula}
        />
      )}
    </Card>
  );
}; 